import { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { BsFillTrashFill } from "react-icons/bs";
import { RiPencilLine } from "react-icons/ri";
import { Eye, Download, UserPlus, MoreVertical, Copy } from "lucide-react";
import toast from "react-hot-toast";
import { XCircle } from "lucide-react";
import Header from "../Header";
import LeftNavBar from "../LeftNavBar";
import PageHeader from "../PageHeader";
import { PageHeaderName } from "../../StyledCss/styledComponents";
import { PrimaryButton, SecondaryButton } from "../../StyledCss/styledbtn";
import SlidingModal from "../SlidingModal/slidingModal";
import AddProjectPopup from "../AddProjectPopup";
import PopupModal from "../PopupModal";
import AddProjectAssignmentForm from "../AddUserForm/addUserForm";
import LoadingView from "../LoadingView";
import FailureView from "../FailureView";
import { ErrorText } from "../../StyledCss/styledComponents";
import { convertKeysToSnakeCase } from "../../util/convertToSnakeCase";
import {
  getProjects,
  deleteProject,
  addProject,
  updateProject,
  assignUserToProject,
  getDownloadInvoice,
} from "../../api/services/hroneApi";
import { convertKeysToCamelCase } from "../../util/convertCamelCase";
import { usePermissions } from "../../redux/slices/permissionsSlice";
import { FEATURE_CODES } from "../../util/featureCodes";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

const MonthsData = [
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

const generateYearsList = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = currentYear - 10; i <= currentYear + 5; i++) {
    years.push({ value: i.toString(), label: i.toString() });
  }
  return years;
};

const yearsList = generateYearsList();

const apiStatusConstants = {
  initial: "INITIAL",
  success: "SUCCESS",
  failure: "FAILURE",
  inProgress: "IN_PROGRESS",
};

const STATUS_STYLES = {
  ACTIVE: { color: "green", fontWeight: "bold" },
  INACTIVE: { color: "red", fontWeight: "bold" },
  CLOSED: { color: "red", fontWeight: "bold" },

  terminated: { color: "orange", fontWeight: "bold" },
  default: { color: "blue", fontWeight: "bold" },
};

const Projects = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { hasPermission } = usePermissions();
  const canViewProject = hasPermission(FEATURE_CODES.PROJECT_VIEW);
  const canCreateProject = hasPermission(FEATURE_CODES.PROJECT_CREATE);
  const canEditProject = hasPermission(FEATURE_CODES.PROJECT_EDIT);
  const canDeleteProject = hasPermission(FEATURE_CODES.PROJECT_DELETE);
  const canCloneProject = hasPermission(FEATURE_CODES.PROJECT_CLONE);
  const canAssignEmployee = hasPermission(FEATURE_CODES.PROJECT_EMPLOYEE_ASSIGN);
  const canDownloadInvoice = hasPermission(FEATURE_CODES.PROJECT_INVOICE_DOWNLOAD);
  const [apiStatus, setApiStatus] = useState(apiStatusConstants.initial);
  const [projectsData, setProjectsData] = useState([]);

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isCloneMode, setIsCloneMode] = useState(false);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [deleteWorklogCount, setDeleteWorklogCount] = useState(false);
  const [forceDelete, setForceDelete] = useState(false);
  const [isCheckingDelete, setIsCheckingDelete] = useState(false);
  const [dropdownOpenProject, setDropdownOpenProject] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  const [isOffboardModalOpen, setIsOffboardModalOpen] = useState(false);
  const [projectToClose, setProjectToClose] = useState(null);

  const [isUserPopupOpen, setIsUserPopupOpen] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);

  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [projectToDownload, setProjectToDownload] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [closingDate, setClosingDate] = useState("");
  const [closingDateError, setClosingDateError] = useState("");
  const [errorMessages, setErrorMessages] = useState({
    addProject: "",
    updateProject: "", 
    assignUser: "",
    downloadInvoice: "",
    deleteProject: "",
  });

  const [loadingStates, setLoadingStates] = useState({
    addProject: false,
    updateProject: false,
    deleteProject: false,
    assignUser: false,
    downloadInvoice: false,
    closeProject: false,
  });

  const setLoading = (operation, isLoading) => {
    setLoadingStates((prev) => ({ ...prev, [operation]: isLoading }));
  };


  const getProjectsList = useCallback(async () => {
    try {
      setApiStatus(apiStatusConstants.inProgress);
      const response = id ? await getProjects(id) : await getProjects();

      if (response.ok) {
        const data = convertKeysToCamelCase(await response.json());
        setProjectsData(data);
        setApiStatus(apiStatusConstants.success);
      } else {
        setApiStatus(apiStatusConstants.failure);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
      setApiStatus(apiStatusConstants.failure);
    }
  }, [id]);

  useEffect(() => {
    getProjectsList();
  }, [getProjectsList]);

  const onClickRetry = useCallback(() => getProjectsList(), [getProjectsList]);

  const addNewProject = async (newProject) => {
      try {
        setLoading("addProject", true);

        const payload = { ...newProject, clone: isCloneMode ? true : false };
        const response = await addProject(convertKeysToSnakeCase(payload));
        if (response.ok) {
          toast.success("Project Added Successfully");
          await getProjectsList();
          setIsPopupOpen(false);
        } else {
          const data=await response.json();
          setErrorMessages(prev => ({...prev, addProject: data.detail}));
        }
      } catch (error) {
        toast.error("Project Cannot be Added");
        console.error("Error adding project:", error);
      } finally {
        setLoading("addProject", false);
      }
    }
    const handleUpdateProject = async (updatedProject) => {
      try {
        setLoading("updateProject", true);

        const response = await updateProject(convertKeysToSnakeCase(updatedProject));
        if (response.ok) {
          toast.success("Project Updated Successfully");
          await getProjectsList();
          setIsPopupOpen(false);
          setIsEditMode(false);
          setEditingProject(null);
        } else {
          const data=await response.json();
          setErrorMessages(prev => ({...prev, updateProject: data.detail}));
        }
      } catch (error) {
        toast.error("Failed to update project");

        console.error("Error updating project:", error);
      } finally {
        setLoading("updateProject", false);
      }
    }
  const cancelDelete = () => {
    setIsOffboardModalOpen(false);
    setClosingDate("");
    setClosingDateError("");
  };
  const handleClose = (project) => {
    setProjectToClose(project);
    setIsOffboardModalOpen(true);
  };

  const handleDeleteProject = async (project) => {
    setProjectToDelete(project);
    setIsDeleteModalOpen(true);
    // setIsCheckingDelete(true);
    
    // try {
    //   const response = await deleteProject(project.id, false);
    //   const data = await response.json();

    //   if (data.count === true) {
    //     setDeleteWorklogCount(true);
    //   }
    // } catch (error) {
    //   console.error("Error checking delete:", error);
    // } finally {
    //   setIsCheckingDelete(false);
    // }
  };

  const handleDownloadClick = useCallback((project) => {
    const currentDate = new Date();
    const currentMonth = String(currentDate.getMonth() + 1).padStart(2, "0");
    const currentYear = String(currentDate.getFullYear());

    setProjectToDownload(project);
    setSelectedMonth(currentMonth);
    setSelectedYear(currentYear);
    setErrorMessages(prev => ({...prev, downloadInvoice: ""}));
    setIsDownloadModalOpen(true);
  }, []);

  const handleDownloadInvoice = useCallback(async () => {
    if (!selectedMonth || !selectedYear) {
      toast.error("Please select both month and year");
      return;
    }

    try {
      setLoading("downloadInvoice", true);
      const payload = {
        id: projectToDownload.id,
        month: selectedMonth,
        year: selectedYear,
      };

      const response = await getDownloadInvoice(payload);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        const fileName = `Invoice_${projectToDownload.name}`;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        setIsDownloadModalOpen(false);
        setProjectToDownload(null);
        toast.success("Invoice downloaded successfully");
      } else {
        const data = await response.json();
        setErrorMessages(prev => ({...prev, downloadInvoice: data.detail}));
      }
    } catch (error) {
      console.error("Error downloading invoice:", error);
      toast.error("Failed to download invoice");
    } finally {
      setLoading("downloadInvoice", false);
    }
  }, [projectToDownload, selectedMonth, selectedYear]);

  const cancelDownload = useCallback(() => {
    setIsDownloadModalOpen(false);
    setProjectToDownload(null);
    setSelectedMonth("");
    setSelectedYear("");
    setErrorMessages(prev => ({...prev, downloadInvoice: ""}));    
  }, []);

  const confirmDeleteProject = async () => {
    try {
      setLoading("deleteProject", true);
      const response = await deleteProject(projectToDelete.id, deleteWorklogCount ? forceDelete : true);
      if (response.status === 200) {
        toast.success("Project Deleted Successfully");
        await getProjectsList();
        setIsDeleteModalOpen(false);
        setProjectToDelete(null);
        setDeleteWorklogCount(false);
        setForceDelete(false);
      } else {
        const data = await response.json();
        setErrorMessages(prev => ({...prev, deleteProject: data.detail}));
      }
    } catch (error) {
      console.error("Error deleting project:", error);
      toast.error("Failed to delete project");
    } finally {
      setLoading("deleteProject", false);
    }
  }
  const cancelDeleteProject = () => {
    setIsDeleteModalOpen(false);
    setProjectToDelete(null);
    setDeleteWorklogCount(false);
    setForceDelete(false);
    setIsCheckingDelete(false);
    setErrorMessages(prev => ({...prev, deleteProject: ""}));
  };

  const editProject = useCallback((project) => {
    setEditingProject(project);
    setIsEditMode(true);
    setErrorMessages(prev => ({...prev, addProject: "", updateProject: ""}));
    setIsPopupOpen(true);
  }, []);

  const cloneProject = (project) => {
    const { id, employeeCount, startDate, status, ...cloneData } = project;
    setEditingProject(cloneData);
    setIsEditMode(false);
    setIsCloneMode(true);
    setErrorMessages(prev => ({...prev, addProject: "", updateProject: ""}));
    setIsPopupOpen(true);
  }

  const openAddModal = useCallback(() => {
    setIsEditMode(false);
    setEditingProject(null);
    setErrorMessages(prev => ({...prev, addProject: "", updateProject: ""}));
    setIsPopupOpen(true);
  }, []);

  const toggleIsPopupOpen = useCallback(() => {
    setIsPopupOpen(false);
    setIsEditMode(false);
    setIsCloneMode(false);
    setEditingProject(null); 
    setErrorMessages(prev => ({...prev, addProject: "", updateProject: ""}));
  }, []);
  const handleDateSubmit = async () => {
    if (!closingDate || !projectToClose) return;

    try {
      setLoading("closeProject", true);
      const updatedProject = {
        ...projectToClose,
        endDate: closingDate,
        status: "CLOSED",
      };
      const response = await updateProject(convertKeysToSnakeCase(updatedProject));
      if (response.ok) {
        await getProjectsList();
        setIsOffboardModalOpen(false);
        setClosingDate("");
        setProjectToClose(null);
        toast.success("Project Updated successfully");
      } 
      else {
          const data=await response.json();
          setErrorMessages(prev => ({...prev, updateProject: data.detail}));
      }
    } catch (error) {
      console.error("Error updating project:", error);
      toast.error("Failed to update project");
    } finally {
      setLoading("closeProject", false);
    }
  };
  const handleUserSubmit = useCallback(
    async (userData) => {
      const payload = {
        projectId: selectedProjectId,
        ...userData,
      };

      try {
        setLoading("assignUser", true);

        const response = await assignUserToProject(convertKeysToSnakeCase(payload));
        if (response.ok) {
          await getProjectsList();
          setIsUserPopupOpen(false);
          setSelectedProjectId(null);
          setSelectedProject(null);
          toast.success("Employee assigned successfully");
        } else {
          const errorData = await response.json();
          setErrorMessages(prev => ({...prev, assignUser: errorData.detail}));
          console.error("API Error:", errorData);
        }
      } catch (error) {
        console.error("Error assigning user:", error);
        toast.error("Failed to assign employee");
      } finally {
        setLoading("assignUser", false);
      }
    },
    [selectedProjectId, getProjectsList],
  );

  const handleUserCancel = useCallback(() => {
    setIsUserPopupOpen(false);
    setSelectedProjectId(null);
    setSelectedProject(null);
    setErrorMessages(prev => ({...prev, assignUser: ""}));
  }, []);
  useEffect(() => {
    const handleClickOutside = () => {
      setDropdownOpenProject(null);
    };

    if (dropdownOpenProject) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [dropdownOpenProject]);
  const handleViewUsers = useCallback(
    (projectId) => {
      navigate(`/project/project_details/${projectId}`);
    },
    [navigate],
  );

  const getStatusStyle = useCallback((status) => {
    return STATUS_STYLES[status] || STATUS_STYLES.default;
  }, []);

  const handleAddUserClick = useCallback((project) => {
    setSelectedProjectId(project.id);
    setSelectedProject(project);
    setErrorMessages(prev => ({...prev, assignUser: ""}));
    setIsUserPopupOpen(true);
  }, []);

  const defaultColDef = useMemo(
    () => ({
      cellStyle: { fontSize: "14px" },
      sortable: true,
      filter: true,
      floatingFilter: true,
    }),
    [],
  );

  const columnDefs = useMemo(
    () => [
      {
        headerName: "PROJECT NAME",
        field: "name",
        width: 250,
        pinned: "left", 
        suppressMenu: true
      },
      {
        headerName: "CLIENT NAME",
        field: "clientName",
        width: 250,
        cellRenderer: (params) => params.value || "-",
        cellStyle: { textAlign: "center" },
        suppressMenu: true
      },
      {
        headerName: "START DATE",
        field: "startDate",
        width: 200,
        suppressMenu: true
      },
      {
        headerName: "END DATE",
        field: "endDate",
        width: 200,
        cellRenderer: (params) => params.value || "-",
        cellStyle: { textAlign: "center" },
        suppressMenu: true
      },
      {
        headerName: "STATUS",
        field: "status",
        width: 250,
        cellStyle: (params) => getStatusStyle(params.value),
        suppressMenu: true
      },
      {
        headerName: "NO OF EMPLOYEES",
        field: "employeeCount",
        width: 200,
        suppressMenu: true
      },
      {
        headerName: "ACTIONS",
        width: 250,
        cellRenderer: (params) => {
          const hasEmployees = params.data.employeeCount > 0;
          const isClosedStatus = params.data.status === "CLOSED";

          return (
            <div
              style={{
                display: "flex",
                gap: "10px",
                alignItems: "center",
                height: "100%",
              }}
            >
              {canViewProject && (
                <div
                  title={hasEmployees ? "View" : "No employees to view"}
                  className="icon-wrapper"
                >
                  <Eye
                    style={{
                      cursor: "pointer",color:"#007bff"
                    }}
                    onClick={() =>
                      handleViewUsers(params.data.id)
                    }
                  />
                </div>
              )}
              {canAssignEmployee && (
                <div title="Assign Employee" className="icon-wrapper">
                  <UserPlus
                    style={{
                      cursor: isClosedStatus ? "not-allowed " : "pointer",
                      color: isClosedStatus ? "#cccccc" : "#5900ffff",
                    }}
                    onClick={() =>
                      !isClosedStatus && handleAddUserClick(params.data)
                    }
                  />
                </div>
              )}
              <div title="More" className="icon-wrapper" style={{ position: 'relative' }}>
                <MoreVertical 
                  style={{
                    cursor: "pointer",
                    color: "#007bff"
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    const rect = e.target.getBoundingClientRect();
                    setDropdownPosition({ 
                      top: rect.bottom+5, 
                      left: rect.left 
                    });
                    setDropdownOpenProject(dropdownOpenProject === params.data.id ? null : params.data.id);
                  }}
                />
              </div>
            </div>
          );
        },
        sortable: false,
        filter: false,
        floatingFilter: false,
      },
    ],
    [
      getStatusStyle,
      handleViewUsers,
      handleAddUserClick,
      dropdownOpenProject,
      setDropdownOpenProject,
      setDropdownPosition,
      canAssignEmployee,
      canViewProject,
    ],
  );

  const renderTable = useCallback(
    () => (
      <div
        className="ag-theme-alpine"
        style={{
          height: "75vh",
          fontSize: "14px",
          marginLeft: "20px",
          marginRight: "20px",
          fontFamily: "Arial, Helvetica, sans-serif",
          backgroundColor: "#FFFFFF",
        }}
      >
        <AgGridReact
          columnDefs={columnDefs}
          rowData={projectsData}
          pagination={true}
          paginationPageSize={20}
          defaultColDef={defaultColDef}
          rowSelection="multiple"
          suppressRowClickSelection={true}
          enableCellTextSelection={true}
        />
      </div>
    ),
    [columnDefs, projectsData, defaultColDef],
  );

  const renderData = () => {
    switch (apiStatus) {
      case apiStatusConstants.inProgress:
        return <LoadingView />;
      case apiStatusConstants.success:
        return renderTable();
      case apiStatusConstants.failure:
        return <FailureView onClickRetry={onClickRetry} />;
      default:
        return null;
    }
  };

  const minClosingDate = projectToClose?.startDate
    ? new Date(new Date(projectToClose.startDate).getTime() + 86400000).toISOString().split('T')[0]
    : '';

  const handleClosingDateChange = (e) => {
    const val = e.target.value;
    if (val && val < minClosingDate) {
      setClosingDateError(`Date must be after start date ${minClosingDate}`);
    } else {
      setClosingDateError("");
    }
    setClosingDate(val);
  };

  return (
    <div>

      <Header />
      <div className="d-flex"> 
        <LeftNavBar />
        <div className="page-content-wrapper">
          <PageHeader>
            <PageHeaderName>Projects</PageHeaderName>
            {canCreateProject && (
              <PrimaryButton onClick={openAddModal}>Add Project</PrimaryButton>
            )}
          </PageHeader>
          <div className="table-container">{renderData()}</div>
          
          {/* Dropdown rendered outside table */}
          {dropdownOpenProject && (
            <div style={{
              position: 'fixed',
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              backgroundColor: 'white',
              border: '1px solid #ddd',
              borderRadius: '4px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              zIndex: 99999,
              minWidth: '120px',
              padding: '4px 0'
            }}>
              {(() => {
                const project = projectsData.find(p => p.id === dropdownOpenProject);
                return canEditProject && project?.status !== "CLOSED" && (
                  <div onClick={() => {
                    editProject(project);
                    setDropdownOpenProject(null);
                  }} style={{ padding: '4px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <RiPencilLine style={{ color: "#28a745", fontSize: "16px" }} /> Edit
                  </div>
                );
              })()}
              {canDownloadInvoice && (
                <div onClick={() => {
                  const project = projectsData.find(p => p.id === dropdownOpenProject);
                  handleDownloadClick(project);
                  setDropdownOpenProject(null);
                }} style={{ padding: '4px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Download size={16} style={{ color: "#5900ffff" }} /> Download
                </div>
              )}
              {(() => {
                const project = projectsData.find(p => p.id === dropdownOpenProject);
                const isClosedStatus = project?.status === "CLOSED";
                return (
                  <>
                    {canCloneProject && (
                      <div onClick={() => {
                        cloneProject(project);
                        setDropdownOpenProject(null);
                      }} style={{ padding: '4px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Copy size={16} style={{ color: "#28a745" }} /> Clone
                      </div>
                    )}
                    {!isClosedStatus && canEditProject && (
                      <div onClick={() => {
                        handleClose(project);
                        setDropdownOpenProject(null);
                      }} style={{ padding: '4px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <XCircle size={16} style={{ color: "#dc3545" }} /> Close
                      </div>
                    )}
                  </>
                );
              })()}
              {canDeleteProject && (
                <div onClick={() => {
                  const project = projectsData.find(p => p.id === dropdownOpenProject);
                  handleDeleteProject(project);
                  setDropdownOpenProject(null);
                }} style={{ padding: '4px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <BsFillTrashFill style={{ color: "#dc3545", fontSize: "16px" }} /> Delete
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Project Modal */}
      <SlidingModal
        modalName={isEditMode ? "Edit Project" : isCloneMode ? "Clone Project" : "Add Project"}
        closeBtn={toggleIsPopupOpen}
        isModalOpen={isPopupOpen}
      >
        <AddProjectPopup
          popupOpen={isPopupOpen}
          toggleIsPopupOpen={toggleIsPopupOpen}
          closeBtn={toggleIsPopupOpen}
          onSubmitData={isEditMode ? handleUpdateProject : addNewProject}
          defaultValues={editingProject}
          isEditMode={isEditMode}
          isClosedStatus={isEditMode && editingProject?.status === "CLOSED"}
          isCloneMode={isCloneMode}
          isLoading={loadingStates.addProject || loadingStates.updateProject}
          errorMsg={errorMessages.addProject || errorMessages.updateProject}

        />
      </SlidingModal>

      {/* Add User Modal */}
      <PopupModal
        showModal={isUserPopupOpen}
        modalName={`Assign Employee to ${selectedProject?.name}`}
        projectName={selectedProject?.name}
        closeBtn={handleUserCancel}
      >
        <AddProjectAssignmentForm
          onSubmit={handleUserSubmit}
          onCancel={handleUserCancel}
          selectedProjectId={selectedProject?.id}
          projectStartDate={selectedProject?.start_date}
          isLoading={loadingStates.assignUser}
          errorMsg={errorMessages.assignUser}
          onClearError={() => setErrorMessages(prev => ({...prev, assignUser: ""}))}
        />
      </PopupModal>

      {/* Delete Confirmation Modal */}
      <div className="no-modal-mb">
        <PopupModal
          showModal={isDeleteModalOpen}
          modalName="Confirm Delete"
          closeBtn={cancelDeleteProject}
          marginBottom="0px"
        >
          <div>
            <p>Are you sure you want to delete this project?</p>
            {errorMessages.deleteProject && (
              <ErrorText className="mt-3">*{errorMessages.deleteProject}</ErrorText>
            )}                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    justifyContent: "end",
                    marginTop: "15px",
                  }}
                >
                  <SecondaryButton onClick={cancelDeleteProject}>Cancel</SecondaryButton>
                  <PrimaryButton
                    onClick={confirmDeleteProject}
                    disabled={isCheckingDelete || loadingStates.deleteProject || errorMessages.deleteProject}
                  >
                    {loadingStates.deleteProject ? "Deleting..." : "Delete"}
                  </PrimaryButton>
                </div>
          </div>
          {/* <div>
            {isCheckingDelete ? (
              <p>Loading.....</p>
            ) : deleteWorklogCount ? (
              <>
                <p style={{ maxWidth: "550px" }}>
                  The "{projectToDelete?.name}" project has existing worklogs. Deleting the project will remove all of those worklogs as well.
                </p>
                <div style={{ marginBottom: "1rem", display: "flex", alignItems: "center", justifyContent: "end" }}>
                  <input
                    type="checkbox"
                    id="forceDeleteCheck"
                    checked={forceDelete}
                    onChange={(e) => setForceDelete(e.target.checked)}
                    style={{ marginRight: "8px", cursor: "pointer" }}
                  />
                  <span style={{ cursor: "pointer" }} onClick={() => setForceDelete(!forceDelete)}>
                    Force delete?
                  </span>
                </div>
                {forceDelete && (
                  <div
                    style={{
                      display: "flex",
                      gap: "10px",
                      justifyContent: "end",
                      marginTop: "15px",
                    }}
                  >
                    <SecondaryButton onClick={cancelDeleteProject}>Cancel</SecondaryButton>
                    <PrimaryButton
                      onClick={confirmDeleteProject}
                      disabled={loadingStates.deleteProject}
                    >
                      {loadingStates.deleteProject ? "Deleting..." : "Delete"}
                    </PrimaryButton>
                  </div>
                )}
              </>
            ) : (
              <>
                <p>Are you sure you want to delete this project?</p>
            {errorMessages.deleteProject && (
              <ErrorText className="mt-3">*{errorMessages.deleteProject}</ErrorText>
            )}                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    justifyContent: "end",
                    marginTop: "15px",
                  }}
                >
                  <SecondaryButton onClick={cancelDeleteProject}>Cancel</SecondaryButton>
                  <PrimaryButton
                    onClick={confirmDeleteProject}
                    disabled={isCheckingDelete || loadingStates.deleteProject}
                  >
                    {loadingStates.deleteProject ? "Deleting..." : "Delete"}
                  </PrimaryButton>
                </div>
              </>
            )}
          </div> */}
        </PopupModal>
      </div>

      {/* Download Modal */}
      <PopupModal
        showModal={isDownloadModalOpen}
        modalName="Download Invoice"
        closeBtn={cancelDownload}
        marginBottom="0px"
      >
        <div>
          <p style={{ marginBottom: "20px" }}>
            Download invoice for project "{projectToDownload?.name}"
          </p>
          <div className="d-flex flex-row flex-wrap justify-content-end align-items-center mb-2">
            <div className="month-picker d-flex flex-column m-1">
              <label className="mb-1">Select Month</label>
              <select
                className="selectElement"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
              >
                <option value="">Select Month</option>
                {MonthsData.map((eachMonth) => (
                  <option key={eachMonth.value} value={eachMonth.value}>
                    {eachMonth.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="month-picker d-flex flex-column m-1">
              <label className="mb-1">Select Year</label>
              <select
                className="selectElement"
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                <option value="">Select Year</option>
                {yearsList.map((eachYear) => (
                  <option key={eachYear.value} value={eachYear.value}>
                    {eachYear.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
           {errorMessages.downloadInvoice && (
                          <ErrorText className="mt-3">*{errorMessages.downloadInvoice}</ErrorText>
                        )}
          <div
            style={{
              display: "flex",
              gap: "10px",
              justifyContent: "center",
            }}
          >
            <SecondaryButton onClick={cancelDownload}>Cancel</SecondaryButton>
            <PrimaryButton
              style={{ width: "200px" }}
              onClick={handleDownloadInvoice}
              disabled={loadingStates.downloadInvoice}
            >
              {loadingStates.downloadInvoice
                ? "Generating..."
                : "Generate and Download"}
            </PrimaryButton>
          </div>
        </div>
      </PopupModal>
      {/* Closing the Project */}
      <div className="no-modal-mb">
        <PopupModal
          showModal={isOffboardModalOpen}
          modalName="Confirm Closing Project"
          closeBtn={cancelDelete}
          marginBottom="0px"
        >
          <div>
            <p>Select closing date:</p>
            <input
              type="date"
              value={closingDate}
              min={minClosingDate}
              onChange={handleClosingDateChange}
              className="form-control mb-3"
              style={closingDateError ? { borderLeft: "10px solid red" } : {}}
              max="9999-12-31"

            />
            {closingDateError && (
              <ErrorText className="mt-1">*{closingDateError}</ErrorText>
            )}
            {errorMessages.updateProject && (
              <ErrorText className="mt-3">*{errorMessages.updateProject}</ErrorText>
            )}
            <div
              style={{
                display: "flex",
                gap: "10px",
                justifyContent: "end",
                marginTop: "15px",
              }}
            >
              <SecondaryButton onClick={cancelDelete}>
                Cancel
              </SecondaryButton>
              <PrimaryButton
                onClick={handleDateSubmit}
                disabled={!closingDate || closingDateError}
              >
                {loadingStates.closeProject ? "Confirming..." : "Confirm"}{" "}
              </PrimaryButton>
            </div>
          </div>
        </PopupModal>
      </div>
    </div>
  );
};

export default Projects;
